import { z } from 'zod/v4';

// ==========================================
// AI CONFIG
// ==========================================

export const aiConfigSchema = z.object({
  key: z.string().min(1, 'Ключ обязателен'),
  value: z.record(z.string(), z.unknown()),
  isActive: z.boolean().optional(),
});

export const updateAiConfigSchema = z.object({
  value: z.record(z.string(), z.unknown()),
  isActive: z.boolean().optional(),
});

// ==========================================
// CHAT
// ==========================================

export const chatSchema = z.object({
  message: z.string()
    .min(1, 'Сообщение не может быть пустым')
    .max(10000, 'Сообщение слишком длинное (максимум 10000 символов)'),
  // userId намеренно удалён: идентификатор пользователя берётся только из JWT-токена
});

// ==========================================
// AI AGENT
// ==========================================

export const aiAgentTranslateSchema = z.object({
  entityType: z.string()
    .min(1, 'Тип сущности обязателен'),
  entityId: z.number()
    .int('ID сущности должен быть целым числом'),
  sourceLanguage: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(10, 'Код языка слишком длинный'),
  targetLanguage: z.string()
    .min(2, 'Код языка должен быть минимум 2 символа')
    .max(10, 'Код языка слишком длинный'),
  fields: z.array(z.string().min(1, 'Название поля обязательно')).optional(),
  sourceData: z.record(z.string(), z.string()).optional(),
  modelOverride: z.string().min(1, 'Название модели обязательно').optional(),
  dryRun: z.boolean().optional(),
});

// ==========================================
// ADMIN CHAT
// ==========================================

export const createAiChatSchema = z.object({
  title: z.string().max(200, 'Название слишком длинное (максимум 200 символов)').optional(),
});

export const updateAiChatTitleSchema = z.object({
  title: z.string()
    .min(1, 'Название обязательно')
    .max(200, 'Название слишком длинное (максимум 200 символов)'),
});

export const sendAiChatMessageSchema = z.object({
  message: z.string()
    .min(1, 'Сообщение не может быть пустым')
    .max(50000, 'Сообщение слишком длинное'),
  model: z.string().min(1, 'Модель обязательна').optional(),
  pageContext: z.string().optional(),
  routerEnabled: z.boolean().optional(),
  routerModel: z.string().min(1, 'Модель роутера обязательна').optional(),
  attachments: z.array(z.object({
    id: z.string().min(1, 'ID вложения обязателен'),
    url: z.string().url('Некорректный URL'),
    mimeType: z.string().min(1, 'MIME-тип обязателен'),
  })).optional(),
  webSearchEnabled: z.boolean().optional(),
  selectedDocumentIds: z.array(z.number().int('ID документа должен быть целым числом')).optional(),
});

export const editAiChatMessageSchema = z.object({
  content: z.string({ message: 'Содержимое обязательно' })
    .min(1, 'Содержимое не может быть пустым'),
});

export const switchAiChatBranchSchema = z.object({
  branchIndex: z.number({ message: 'Индекс ветки обязателен' })
    .int('Индекс ветки должен быть целым числом')
    .min(0, 'Индекс ветки не может быть отрицательным'),
});

// ==========================================
// TYPES
// ==========================================

export type AIConfigDto = z.infer<typeof aiConfigSchema>;
export type UpdateAIConfigDto = z.infer<typeof updateAiConfigSchema>;
export type ChatDto = z.infer<typeof chatSchema>;
export type AiAgentTranslateDto = z.infer<typeof aiAgentTranslateSchema>;

// ==========================================
// RESPONSE TYPES
// ==========================================

/**
 * RAG ответ от AI
 */
export interface RAGResponse {
  answer: string;
  sources?: string[];
  toolsUsed?: string[];
}

/**
 * Vision attachment для сообщений
 */
export interface MessageAttachment {
  id: string;
  url: string;
  mimeType: string;
}

/**
 * Tool call с результатом для debug panel
 */
export interface ToolCallWithResult {
  id: string;
  name: string;
  arguments: string;
  result?: string;
  error?: string;
  duration?: number;
}

/**
 * Ответ Admin AI Agent
 */
export interface AgentResponse {
  answer: string;
  toolsUsed: string[];
  messageId: number;
  warnings?: string[];
  metadata?: {
    model?: string;
    tokens?: {
      prompt?: number;
      completion?: number;
      total?: number;
    };
    duration?: number;
    routerUsed?: boolean;
    routerDecision?: {
      category?: string;
      reasoning?: string;
    };
    toolCalls?: ToolCallWithResult[];
    webSearchUsed?: boolean;
    webSearchSources?: Array<{
      title: string;
      url: string;
    }>;
  };
}
